import type { BodyNodeLink, Link, Node } from '../types';
import type { RawPose, RawPoseResult } from './PoseEngine';

export interface MapMetrics {
  fps: number;
  inferenceTimeMs: number;
}

export function mapPose(
  pose: RawPose,
  nodeNames: readonly string[],
  links: readonly Link[],
  metrics: MapMetrics,
): BodyNodeLink {
  const nodes: Node[] = pose.landmarks.map((lm, idx) => ({
    id: idx,
    name: nodeNames[idx] ?? `node_${idx}`,
    x: lm.x,
    y: lm.y,
    score: lm.score,
    worldX: lm.worldX,
    worldY: lm.worldY,
    worldZ: lm.worldZ,
    worldVisibility: lm.worldVisibility,
  }));
  return {
    nodes,
    links: links.slice() as Link[],
    fps: metrics.fps,
    inferenceTimeMs: metrics.inferenceTimeMs,
  };
}

export function mapRawToBodies(
  raw: RawPoseResult,
  nodeNames: readonly string[],
  links: readonly Link[],
  metrics: MapMetrics,
): BodyNodeLink[] {
  return raw.poses.map((pose) => mapPose(pose, nodeNames, links, metrics));
}

export function filterByScore(bnl: BodyNodeLink, minScore: number): BodyNodeLink {
  const validIds = new Set(bnl.nodes.filter((n) => n.score >= minScore).map((n) => n.id));
  const links = bnl.links.filter((l) => validIds.has(l.sourceId) && validIds.has(l.targetId));
  return { ...bnl, links };
}

export function toPixelCoords(
  node: Node,
  width: number,
  height: number,
): { px: number; py: number } {
  return { px: node.x * width, py: node.y * height };
}
